import { memo, Fragment, useState, useCallback, useEffect } from 'react'
import useDebouncedEffect from 'use-debounced-effect'
import { Optional } from 'packages/types'
import ColorSelect from 'builder/components/ColorSelect'
import { ColorChangeHandler } from 'builder/modules/resumeEditor'
import { ColorPickerControl } from './ColorPickerControl'

type Props = {
  size?: number
  options: string[]
  selected: Optional<string>
  supportsCustomColor?: boolean
  isNewBuilder?: boolean
  onSelect: ColorChangeHandler
  handleColorPickerOpen?: () => void
}

// Wrapper to update and render selected colors instantly.
// Calls the parent's callback with a debounce
export const Colors = memo((props: Props) => {
  const {
    size,
    selected,
    options,
    isNewBuilder,
    supportsCustomColor,
    onSelect,
    handleColorPickerOpen,
  } = props
  const [isOpen, setOpen] = useState(false)
  const [localColor, setLocalColor] = useState<string>()

  // Update local value using incoming props
  useEffect(() => {
    if (selected) {
      setLocalColor(selected)
    }
  }, [selected])

  // Debounce the callback to avoid extra updates and re-renders of `DocumentCustomizationModal`
  useDebouncedEffect(
    () => {
      if (localColor && localColor !== selected) {
        onSelect(localColor)
      }
    },
    350,
    [localColor],
  )

  const handleChange = useCallback((value: string) => setLocalColor(value), [])

  const handlePickerOpen = useCallback(() => {
    setOpen(true)
    handleColorPickerOpen?.()
  }, [handleColorPickerOpen])

  const handlePickerClose = useCallback(() => setOpen(false), [])

  return (
    <Fragment>
      <ColorSelect
        size={size}
        selected={localColor}
        options={options}
        onSelect={onSelect}
        onAdd={supportsCustomColor ? handlePickerOpen : undefined}
        isNewBuilder={isNewBuilder}
      />
      {isOpen && (
        <ColorPickerControl hex={localColor} onChange={handleChange} onClose={handlePickerClose} />
      )}
    </Fragment>
  )
})
